import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Text } from 'react-native-paper';

import { useGetOwnOrganizations } from '@/features/api/hooks/grapevine-organization/useGetOwnOrganizations';
import { useHighestGlobalRole } from '@/features/authorization/provider/useHighestGlobalRole';
import { ListSection } from '@/features/list/components/ListSection';
import { organizationLogoIcon } from '@/features/media/components/avatar/OrganizationLogo';
import { buildOrganizationHeaderTitle } from '@/features/profile-setting/components/OrganizationHeaderTitle';
import { ProfileSettingRoute } from '@/features/profile-setting/navigation/profileSettingRoute';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationListItem } from '@/navigation/components/NavigationListItem';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

const styles = StyleSheet.create({
  title: { letterSpacing: 0.2 },
  subtitle: { marginTop: 2, letterSpacing: 0.6 }
});

export const OrganizationListSection: FC = () => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.PROFILE_SETTING, {
    keyPrefix: 'screens.profile-setting'
  });
  const { t: tTags } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.tagLabels'
  });
  const { t: tRoles } = useTranslation(LocalizationNameSpace.ORGANIZATION, {
    keyPrefix: 'organization.roleLabels'
  });
  const { t: tGlobalRoles } = useTranslation(
    LocalizationNameSpace.ORGANIZATION,
    {
      keyPrefix: 'organization.globalRoleLabels'
    }
  );

  const { data: ownOrgs = [] } = useGetOwnOrganizations();

  // A global role applies platform-wide: if the caller holds one, it is shown as
  // the role for every organization. Otherwise the per-organization direct role
  // (ADMIN/FUNKER) is used.
  const globalRole = useHighestGlobalRole();

  // oxlint-disable-next-line unicorn/no-null
  if (ownOrgs.length === 0) return null;

  return (
    <ListSection
      title={t('organizations')}
      style={{ backgroundColor: 'transparent' }}
      headerBackgroundColor="transparent"
    >
      {ownOrgs.map((organization) => {
        const orgName = organization.name ?? '';
        const primaryTag = organization.tags?.[0];
        const directRoleLabel = organization.role
          ? tRoles(organization.role)
          : undefined;
        const roleLabel = globalRole
          ? tGlobalRoles(globalRole)
          : directRoleLabel;
        const subtitle = buildOrganizationHeaderTitle({
          tagLabel: primaryTag ? tTags(primaryTag) : undefined,
          roleLabel
        });
        const geoAreaName = organization.geoAreaName;

        return (
          <NavigationListItem
            key={organization.id}
            backgroundColor={theme.colors.surfaceContainerLowest}
            screen={ProfileSettingRoute.OrganizationDetail}
            params={{ id: organization.id }}
            title={orgName}
            overwriteTitleNode={
              <>
                <Text
                  variant="bodyLarge"
                  style={[styles.title, { color: theme.colors.onBackground }]}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {orgName}
                </Text>
                {subtitle && (
                  <Text
                    variant="labelSmall"
                    style={[
                      styles.subtitle,
                      { color: theme.colors.onSurfaceVariant }
                    ]}
                  >
                    {subtitle}
                  </Text>
                )}
                {geoAreaName && (
                  <Text
                    variant="labelSmall"
                    style={[
                      styles.subtitle,
                      { color: theme.colors.onSurfaceVariant }
                    ]}
                  >
                    {geoAreaName}
                  </Text>
                )}
              </>
            }
            icon={organizationLogoIcon(organization.logoImage)}
          />
        );
      })}
    </ListSection>
  );
};
